import React, { useState } from 'react';
import {
  Modal,
  Stack,
  Group,
  Avatar,
  Text,
  TextInput,
  Button,
  ActionIcon,
  Loader,
  Tabs,
  Badge,
  Box,
} from '@mantine/core';
import {
  IconSearch,
  IconUserMinus,
  IconUserPlus,
  IconMail,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useDebouncedValue } from '@mantine/hooks';
import { openConfirmDialog } from '../common/ConfirmDialog';
import {
  useUserSearchQuery,
  useAddTravelerMutation,
  useRemoveTravelerMutation,
} from '../../hooks/useTravelerMutations';
import { assignTravelerColor } from '../../utils/trips/travelerColorsUtils';
import { isAdmin as checkIsAdmin } from '../../utils/trips/isAdmin';
import { SEARCH_MIN_LENGTH } from '../../constants';
import type { TripDetail, User, UserSummary } from '../../types';

interface ManageMembershipModalProps {
  opened: boolean;
  onClose: () => void;
  trip: TripDetail;
  currentUser: User | null | undefined;
}

const ManageMembershipModal: React.FC<ManageMembershipModalProps> = ({
  opened,
  onClose,
  trip,
  currentUser,
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  const isAdmin = checkIsAdmin(trip, currentUser?.id);
  const tripId = trip.id.toString();

  const debouncedQuery = useDebouncedValue(searchQuery, 300)[0];
  const searchResults = useUserSearchQuery(debouncedQuery, tripId);
  const addTraveler = useAddTravelerMutation(tripId);
  const removeTraveler = useRemoveTravelerMutation(tripId);

  const handleAdd = async (user: UserSummary) => {
    try {
      await addTraveler.mutateAsync(user.id);
      notifications.show({
        title: 'Traveler added',
        message: `${user.name} has been added to the trip.`,
        color: 'green',
        autoClose: 2000,
      });
      setSearchQuery('');
    } catch {
      // error surfaced via mutation.error / service layer toast
    }
  };

  const handleRemove = (user: UserSummary) => {
    openConfirmDialog({
      name: 'Remove traveler?',
      message: `${user.name} will lose access to this trip and their itinerary will be removed.`,
      onConfirm: async () => {
        try {
          await removeTraveler.mutateAsync(user.id);
          notifications.show({
            title: 'Traveler removed',
            message: `${user.name} has been removed from the trip.`,
            color: 'green',
            autoClose: 2000,
          });
        } catch {
          // error surfaced via mutation.error / service layer toast
        }
      },
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Box>
          <Text fw={500}>Travelers</Text>
          <Text size="xs" c="dimmed">
            {trip.name}
          </Text>
        </Box>
      }
      size="sm"
      data-cy="manage-travelers-modal"
    >
      <Stack gap="md">
        <Stack gap="xs">
          <Text size="xs" fw={500} c="dimmed">
            On this trip
          </Text>
          {trip.memberships.map(membership => {
            const isCurrentUser = membership.traveler.id === currentUser?.id;
            const color = assignTravelerColor(
              trip.memberships,
              membership.traveler.id
            );
            return (
              <Group key={membership.traveler.id} justify="space-between">
                <Group gap="xs">
                  <Avatar size="sm" radius="xl" style={{ background: color }}>
                    {membership.traveler.name.charAt(0)}
                  </Avatar>
                  <Box>
                    <Group gap={6}>
                      <Text size="sm" fw={500}>
                        {membership.traveler.name}
                      </Text>
                      {isCurrentUser && (
                        <Badge variant="default" size="xs">
                          you
                        </Badge>
                      )}
                      {trip.admins.some(
                        a => a.id === membership.traveler.id
                      ) && (
                        <Badge variant="default" size="xs">
                          admin
                        </Badge>
                      )}
                    </Group>
                    <Text size="xs" c="dimmed">
                      @{membership.traveler.username}
                    </Text>
                  </Box>
                </Group>
                {isAdmin && !isCurrentUser && (
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    aria-label={`Remove ${membership.traveler.name}`}
                    data-cy={`remove-traveler-${membership.traveler.id}`}
                    loading={removeTraveler.isPending}
                    onClick={() => handleRemove(membership.traveler)}
                  >
                    <IconUserMinus size={16} />
                  </ActionIcon>
                )}
              </Group>
            );
          })}
        </Stack>

        {isAdmin && (
          <Stack gap="xs">
            <Text size="xs" fw={500} c="dimmed">
              Add a traveler
            </Text>
            <Tabs defaultValue="username">
              <Tabs.List>
                <Tabs.Tab value="username">By username</Tabs.Tab>
                <Tabs.Tab
                  value="email"
                  disabled
                  rightSection={
                    <Badge variant="default" size="xs">
                      coming soon
                    </Badge>
                  }
                  leftSection={<IconMail size={12} />}
                >
                  By email
                </Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="username" pt="sm">
                <TextInput
                  label="Search by username"
                  placeholder="Search by username…"
                  leftSection={<IconSearch size={14} />}
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.currentTarget.value)}
                  data-cy="traveler-search-input"
                />

                {searchResults.isLoading && <Loader size="xs" mt="xs" />}

                {searchResults.data?.map(user => (
                  <Group
                    key={user.id}
                    justify="space-between"
                    mt="xs"
                    p="xs"
                    style={{
                      border: '0.5px solid var(--mantine-color-default-border)',
                      borderRadius: 8,
                    }}
                  >
                    <Group gap="xs">
                      <Avatar size="sm" radius="xl">
                        {user.name.charAt(0)}
                      </Avatar>
                      <Box>
                        <Text size="sm" fw={500}>
                          {user.name}
                        </Text>
                        <Text size="xs" c="dimmed">
                          @{user.username}
                        </Text>
                      </Box>
                    </Group>
                    <Button
                      size="xs"
                      leftSection={<IconUserPlus size={13} />}
                      loading={addTraveler.isPending}
                      data-cy={`add-traveler-${user.id}`}
                      onClick={() => handleAdd(user)}
                    >
                      Add
                    </Button>
                  </Group>
                ))}

                {debouncedQuery.trim().length >= SEARCH_MIN_LENGTH &&
                  searchResults.data?.length === 0 &&
                  !searchResults.isLoading && (
                    <Text size="xs" c="dimmed" mt="xs">
                      No users found for &quot;{debouncedQuery}&quot;
                    </Text>
                  )}

                <Text size="xs" c="dimmed" mt="sm">
                  Added travelers can view and edit their own itinerary on this
                  trip.
                </Text>
              </Tabs.Panel>
            </Tabs>
          </Stack>
        )}
      </Stack>
    </Modal>
  );
};

export default ManageMembershipModal;
